"use client";

import { c as _c } from "react/compiler-runtime";
import { Children, type ReactNode } from "react";
import { HiOutlineDocumentText, HiOutlinePhotograph, HiOutlineX } from "react-icons/hi";
import { cx, focusRing } from "@/lib/tailwind-utils";
import type { EmailAttachmentFile } from "./hooks/use-email-attachments";
interface AttachmentsProps {
  attachments: EmailAttachmentFile[];
  onRemove: (id: string) => void;
  /** `AttachmentChip`s for files the caller owns, listed before the attached ones. */
  children?: ReactNode;
  className?: string;
}
function formatFileSize(bytes: number): string {
  const units = ["B", "KB", "MB", "GB"];
  let unitIndex = 0;
  let size = bytes;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}
function getFileIcon(mimeType: string | null) {
  if (mimeType?.startsWith("image/")) {
    return HiOutlinePhotograph;
  }
  return HiOutlineDocumentText;
}
function truncateFilename(filename: string, maxLength: number = 30): string {
  if (filename.length <= maxLength) return filename;
  const ext = filename.lastIndexOf(".");
  if (ext === -1) {
    return filename.slice(0, maxLength - 3) + "...";
  }
  const extension = filename.slice(ext);
  const name = filename.slice(0, ext);
  const availableLength = maxLength - extension.length - 3;
  if (availableLength <= 0) {
    return filename.slice(0, maxLength - 3) + "...";
  }
  return name.slice(0, availableLength) + "..." + extension;
}
export function AttachmentChip(t0) {
  const $ = _c(24);
  const {
    filename,
    mimeType,
    sizeBytes,
    onRemove
  } = t0;
  let t1;
  if ($[0] !== mimeType) {
    t1 = getFileIcon(mimeType);
    $[0] = mimeType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const FileIcon = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cx("flex items-center gap-2 p-2 rounded-lg", "bg-background-secondary border border-border/50", "group transition-colors hover:border-border");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== FileIcon) {
    t3 = <div className="shrink-0 p-1.5 rounded bg-background"><FileIcon className="h-4 w-4 text-foreground-secondary" /></div>;
    $[3] = FileIcon;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== filename) {
    t4 = truncateFilename(filename);
    $[5] = filename;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== filename || $[8] !== t4) {
    t5 = <div className="text-body-strong text-foreground truncate" title={filename}>{t4}</div>;
    $[7] = filename;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== sizeBytes) {
    t6 = sizeBytes !== null && <div className="text-caption text-foreground-muted">{formatFileSize(sizeBytes)}</div>;
    $[10] = sizeBytes;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <div className="flex-1 min-w-0">{t5}{t6}</div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = `Remove ${filename}`;
  let t10;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cx("shrink-0 p-0.5 rounded hover:bg-hover text-foreground-muted hover:text-foreground transition-colors", focusRing);
    t10 = <HiOutlineX className="h-3.5 w-3.5" />;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  let t11;
  if ($[17] !== onRemove || $[18] !== t8) {
    t11 = <button type="button" onClick={onRemove} aria-label={t8} className={t9}>{t10}</button>;
    $[17] = onRemove;
    $[18] = t8;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t3 || $[22] !== t7) {
    t12 = <div className={t2}>{t3}{t7}{t11}</div>;
    $[20] = t11;
    $[21] = t3;
    $[22] = t7;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
}
export function Attachments(t0) {
  const $ = _c(13);
  const {
    attachments,
    onRemove,
    children,
    className
  } = t0;
  if (attachments.length === 0 && Children.toArray(children).length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cx("border-t border-border  px-3 py-2", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== attachments || $[3] !== onRemove) {
    let t3;
    if ($[5] !== onRemove) {
      t3 = attachment => <AttachmentChip key={attachment.id} filename={attachment.file.name} mimeType={attachment.file.type} sizeBytes={attachment.file.size} onRemove={() => onRemove(attachment.id)} />;
      $[5] = onRemove;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = attachments.map(t3);
    $[2] = attachments;
    $[3] = onRemove;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[7] !== children || $[8] !== t2) {
    t3 = <div className="flex flex-wrap gap-2">{children}{t2}</div>;
    $[7] = children;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t1 || $[11] !== t3) {
    t4 = <div className={t1}>{t3}</div>;
    $[10] = t1;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}